import { useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router';

// Hand-made SVG charts: thin marks, one axis, recessive grid, crosshair + tooltip on hover,
// legend for more than one series, direct text in text colors (never in the series color).

export interface Series {
  key: string;
  label: string;
  color: string;
  values: number[];
}

function useWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => setWidth(Math.floor(entry!.contentRect.width)));
    observer.observe(element);
    setWidth(Math.floor(element.getBoundingClientRect().width));
    return () => observer.disconnect();
  }, []);
  return [ref, width] as const;
}

function niceMax(value: number): number {
  if (value <= 0) return 1;
  const exponent = Math.pow(10, Math.floor(Math.log10(value)));
  const fraction = value / exponent;
  const nice = fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 2.5 ? 2.5 : fraction <= 5 ? 5 : 10;
  return nice * exponent;
}

export interface Marker {
  index: number;
  label: string;
  color?: string;
}

export function TimeSeriesChart({
  x,
  series,
  kind = 'area',
  stacked = false,
  height = 220,
  format = (value) => String(Math.round(value)),
  xFormat = (label) => label,
  tooltipTitle,
  marker = [],
  threshold,
  legend = true,
  yMax,
}: {
  x: string[];
  series: Series[];
  kind?: 'area' | 'line' | 'bar';
  stacked?: boolean;
  height?: number;
  format?: (value: number) => string;
  xFormat?: (label: string) => string;
  tooltipTitle?: (label: string) => string;
  marker?: Marker[];
  threshold?: { value: number; label: string };
  legend?: boolean;
  yMax?: number;
}) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const visible = series.filter((item) => !hidden.has(item.key));

  const padding = { top: 10, right: 12, bottom: 22, left: 48 };
  const innerWidth = Math.max(10, width - padding.left - padding.right);
  const innerHeight = height - padding.top - padding.bottom;
  const count = x.length;

  const { max, stackedValues } = useMemo(() => {
    const cumulative = visible.map(() => new Array<number>(count).fill(0));
    let peak = 0;
    for (let index = 0; index < count; index++) {
      let running = 0;
      visible.forEach((item, position) => {
        const value = item.values[index] ?? 0;
        running = stacked ? running + value : value;
        cumulative[position]![index] = running;
        peak = Math.max(peak, running);
      });
    }
    if (threshold) peak = Math.max(peak, threshold.value);
    return { max: yMax ?? niceMax(peak * 1.08), stackedValues: cumulative };
  }, [visible, count, stacked, threshold, yMax]);

  const band = innerWidth / Math.max(1, count);
  const xAt = (index: number) => (kind === 'bar' ? padding.left + band * index + band / 2 : padding.left + (count <= 1 ? innerWidth / 2 : (innerWidth * index) / (count - 1)));
  const yAt = (value: number) => padding.top + innerHeight - (value / max) * innerHeight;

  const ticks = [0, 0.25, 0.5, 0.75, 1].map((step) => step * max);
  const labelEvery = Math.max(1, Math.ceil(count / Math.max(2, Math.floor(innerWidth / 70))));

  const onMove = (event: React.PointerEvent<SVGRectElement>) => {
    const box = (event.currentTarget as SVGRectElement).getBoundingClientRect();
    const relative = event.clientX - box.left;
    const index = kind === 'bar' ? Math.floor(relative / band) : Math.round((relative / innerWidth) * (count - 1));
    setHover(Math.min(count - 1, Math.max(0, index)));
  };

  const paths = visible.map((item, position) => {
    const top = stackedValues[position]!;
    const bottom = stacked && position > 0 ? stackedValues[position - 1]! : new Array<number>(count).fill(0);
    const line = top.map((value, index) => `${index === 0 ? 'M' : 'L'}${xAt(index).toFixed(1)},${yAt(value).toFixed(1)}`).join('');
    const area = `${line}${bottom
      .map((value, index) => [index, value] as const)
      .reverse()
      .map(([index, value]) => `L${xAt(index).toFixed(1)},${yAt(value).toFixed(1)}`)
      .join('')}Z`;
    return { item, line, area, top, bottom };
  });

  const total = hover !== null ? visible.reduce((sum, item) => sum + (item.values[hover] ?? 0), 0) : 0;
  const tooltipLeft = hover !== null ? Math.min(Math.max(xAt(hover) + 14, 0), Math.max(0, width - 230)) : 0;
  const flip = hover !== null && xAt(hover) > width - 250;

  return (
    <div className="chart" ref={ref}>
      {width > 0 ? (
        <svg width={width} height={height} role="img" aria-label={series.map((item) => item.label).join(', ')}>
          {ticks.map((tick) => (
            <g key={tick}>
              <line className={tick === 0 ? 'axis-line' : 'grid-line'} x1={padding.left} x2={width - padding.right} y1={yAt(tick)} y2={yAt(tick)} />
              <text className="tick" x={padding.left - 8} y={yAt(tick) + 3.5} textAnchor="end">
                {format(tick)}
              </text>
            </g>
          ))}
          {x.map((label, index) =>
            index % labelEvery === 0 ? (
              <text key={label + index} className="tick" x={xAt(index)} y={height - 6} textAnchor="middle">
                {xFormat(label)}
              </text>
            ) : null,
          )}
          {threshold ? (
            <g>
              <line x1={padding.left} x2={width - padding.right} y1={yAt(threshold.value)} y2={yAt(threshold.value)} stroke="var(--critical)" strokeDasharray="4 4" strokeWidth={1} opacity={0.8} />
              <text className="tick" x={width - padding.right} y={yAt(threshold.value) - 5} textAnchor="end" style={{ fill: 'var(--critical-text)' }}>
                {threshold.label}
              </text>
            </g>
          ) : null}
          {kind === 'bar'
            ? paths.map(({ item, top, bottom }) =>
                top.map((value, index) => {
                  const barWidth = Math.max(1, band - Math.min(6, band * 0.28));
                  const y = yAt(value);
                  const h = Math.max(0, yAt(bottom[index]!) - y - (stacked && value > 0 ? 1 : 0));
                  return h > 0 ? (
                    <rect key={`${item.key}-${index}`} x={xAt(index) - barWidth / 2} y={y} width={barWidth} height={h} rx={Math.min(2, barWidth / 3)} fill={item.color} opacity={hover === null || hover === index ? 1 : 0.55} />
                  ) : null;
                }),
              )
            : paths.map(({ item, line, area }) => (
                <g key={item.key}>
                  {kind === 'area' ? <path d={area} fill={item.color} opacity={stacked ? 0.55 : 0.14} /> : null}
                  <path d={line} fill="none" stroke={item.color} strokeWidth={stacked ? 1.25 : 2} strokeLinejoin="round" strokeLinecap="round" />
                </g>
              ))}
          {marker.map((item) => (
            <g key={`${item.index}-${item.label}`}>
              <line className="marker-line" x1={xAt(item.index)} x2={xAt(item.index)} y1={padding.top} y2={padding.top + innerHeight} stroke={item.color ?? 'var(--series-7)'} />
              <text className="marker-label" x={xAt(item.index) + 4} y={padding.top + 9} style={{ fill: 'var(--text-2)' }}>
                {item.label}
              </text>
            </g>
          ))}
          {hover !== null && kind !== 'bar' ? (
            <g>
              <line className="crosshair" x1={xAt(hover)} x2={xAt(hover)} y1={padding.top} y2={padding.top + innerHeight} />
              {paths.map(({ item, top }) => (
                <circle key={item.key} cx={xAt(hover)} cy={yAt(top[hover] ?? 0)} r={4} fill={item.color} stroke="var(--surface)" strokeWidth={2} />
              ))}
            </g>
          ) : null}
          <rect x={padding.left} y={padding.top} width={innerWidth} height={innerHeight} fill="transparent" onPointerMove={onMove} onPointerLeave={() => setHover(null)} />
        </svg>
      ) : (
        <div style={{ height }} />
      )}
      {hover !== null ? (
        <div className="chart-tooltip" style={{ left: flip ? Math.max(0, xAt(hover) - 240) : tooltipLeft, top: 8 }}>
          <div className="chart-tooltip-title">{(tooltipTitle ?? xFormat)(x[hover]!)}</div>
          {visible
            .map((item) => ({ item, value: item.values[hover] ?? 0 }))
            .sort((a, b) => b.value - a.value)
            .slice(0, 10)
            .map(({ item, value }) => (
              <div className="chart-tooltip-row" key={item.key}>
                <i className="swatch" style={{ background: item.color }} />
                <span className="ellipsis">{item.label}</span>
                <b>{format(value)}</b>
              </div>
            ))}
          {stacked && visible.length > 1 ? (
            <div className="chart-tooltip-total">
              <span>Totale</span>
              <span>{format(total)}</span>
            </div>
          ) : null}
        </div>
      ) : null}
      {legend && series.length > 1 ? (
        <div className="legend" style={{ marginTop: 8 }}>
          {series.map((item) => (
            <button
              type="button"
              key={item.key}
              className={`legend-item${hidden.has(item.key) ? ' off' : ''}`}
              onClick={() =>
                setHidden((current) => {
                  const next = new Set(current);
                  if (next.has(item.key)) next.delete(item.key);
                  else next.add(item.key);
                  return next;
                })
              }
            >
              <i style={{ background: item.color }} />
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function Sparkline({ values, color = 'var(--series-1)', height = 28, area = true, max }: { values: number[]; color?: string; height?: number; area?: boolean; max?: number }) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const peak = max ?? Math.max(1, ...values);
  const step = values.length > 1 ? width / (values.length - 1) : width;
  const points = values.map((value, index) => [index * step, height - 2 - (value / peak) * (height - 4)] as const);
  const line = points.map(([px, py], index) => `${index === 0 ? 'M' : 'L'}${px.toFixed(1)},${py.toFixed(1)}`).join('');
  return (
    <div ref={ref} style={{ height, width: '100%' }}>
      {width > 0 && values.length > 0 ? (
        <svg width={width} height={height} aria-hidden="true">
          {area ? <path d={`${line}L${width},${height}L0,${height}Z`} fill={color} opacity={0.14} /> : null}
          <path d={line} fill="none" stroke={color} strokeWidth={1.5} strokeLinejoin="round" />
        </svg>
      ) : null}
    </div>
  );
}

/** Tiny bars (errors per hour): reads as frequency at a glance. */
export function MiniBars({ values, color = 'var(--serious)', height = 22, width = 96 }: { values: number[]; color?: string; height?: number; width?: number }) {
  const peak = Math.max(1, ...values);
  const band = width / Math.max(1, values.length);
  return (
    <svg width={width} height={height} aria-hidden="true" style={{ display: 'block' }}>
      {values.map((value, index) => {
        const h = value > 0 ? Math.max(1.5, (value / peak) * height) : 1;
        return <rect key={index} x={index * band + 0.5} y={height - h} width={Math.max(1, band - 1)} height={h} rx={0.8} fill={value > 0 ? color : 'var(--surface-3)'} />;
      })}
    </svg>
  );
}

export function Meter({ value, max, tone, marker, large }: { value: number; max: number; tone?: 'good' | 'warning' | 'critical' | 'neutral'; marker?: number; large?: boolean }) {
  const ratio = max > 0 ? Math.min(1, value / max) : 0;
  const auto = ratio >= 0.9 ? 'critical' : ratio >= 0.75 ? 'warning' : 'good';
  return (
    <div className={`meter${large ? ' lg' : ''}`} role="meter" aria-valuenow={value} aria-valuemin={0} aria-valuemax={max}>
      <i className={tone ?? auto} style={{ width: `${ratio * 100}%` }} />
      {marker !== undefined && max > 0 ? <b style={{ left: `${Math.min(100, (marker / max) * 100)}%` }} /> : null}
    </div>
  );
}

export function BarList({ rows, format, max }: { rows: Array<{ key: string; label: ReactNode; value: number; color?: string; href?: string; part?: Array<{ value: number; color: string }> }>; format: (value: number) => string; max?: number }) {
  const peak = max ?? Math.max(1e-9, ...rows.map((row) => row.value));
  return (
    <div className="barlist">
      {rows.map((row) => {
        const body = (
          <>
            <div className="barlist-label">{row.label}</div>
            <div className="barlist-value">{format(row.value)}</div>
            <div className="barlist-track">
              {row.part ? (
                row.part.map((part, index) => <i key={index} style={{ width: `${(part.value / peak) * 100}%`, background: part.color }} />)
              ) : (
                <i style={{ width: `${(row.value / peak) * 100}%`, background: row.color ?? 'var(--series-1)' }} />
              )}
            </div>
          </>
        );
        return row.href ? (
          <Link key={row.key} className="barlist-row" to={row.href}>
            {body}
          </Link>
        ) : (
          <div key={row.key} className="barlist-row">
            {body}
          </div>
        );
      })}
    </div>
  );
}
